import { DetailedHTMLProps, FC, HTMLAttributes, ReactNode, useEffect, useRef } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { useGoogleMapContext } from '../../context/GoogleMapContext';

/* -------------------------------------------------------------------------------------------------
 * Component: GoogleMapAdvancedMarker
 * -----------------------------------------------------------------------------------------------*/

interface GoogleMapAdvancedMarkerProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    children: ReactNode;
    position: google.maps.LatLng | google.maps.LatLngLiteral;
    onClick?: () => void;
    draggable?: boolean;
    onMarkerDragEnd?: (position: google.maps.LatLngLiteral) => void;
    title?: string;
    zIndex?: number;
}

const GoogleMapAdvancedMarker: FC<GoogleMapAdvancedMarkerProps> = (props) => {
    const { children, position, onClick, draggable = false, onMarkerDragEnd, title, zIndex = 10, ...rest } = props;

    const { map } = useGoogleMapContext();

    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const rootRef = useRef<Root | null>(null);
    const isMountedRef = useRef(true);
    const dragEndListenerRef = useRef<google.maps.MapsEventListener | null>(null);

    // Initialize marker once
    useEffect(() => {
        if (!map) return;

        isMountedRef.current = true;

        // Create content div
        contentRef.current = document.createElement('div');

        // Create React root
        rootRef.current = createRoot(contentRef.current);

        // Create marker
        markerRef.current = new google.maps.marker.AdvancedMarkerElement({
            map,
            position: position,
            content: contentRef.current,
            zIndex: zIndex,
            title: title,
            gmpDraggable: draggable,
        });

        const listener = markerRef.current.addListener('gmp-click', (e: any) => {
            onClick?.();
        });

        return () => {
            isMountedRef.current = false;
            listener.remove();

            // Cleanup drag end listener
            if (dragEndListenerRef.current) {
                google.maps.event.removeListener(dragEndListenerRef.current);
                dragEndListenerRef.current = null;
            }

            // Cleanup marker
            if (markerRef.current) {
                markerRef.current.map = null;
                markerRef.current = null;
            }

            // Cleanup root asynchronously
            if (rootRef.current) {
                const root = rootRef.current;
                rootRef.current = null;
                setTimeout(() => {
                    root.unmount();
                }, 0);
            }

            contentRef.current = null;
        };
    }, [map, onClick, position, draggable, title, zIndex]);

    // Update marker position
    useEffect(() => {
        if (markerRef.current && isMountedRef.current) {
            markerRef.current.position = position;
        }
    }, [position]);

    // Update draggable property
    useEffect(() => {
        if (markerRef.current && isMountedRef.current) {
            markerRef.current.gmpDraggable = draggable;
        }
    }, [draggable]);

    // Update drag end handler
    useEffect(() => {
        if (!markerRef.current || !isMountedRef.current) return;

        // Remove previous listener
        if (dragEndListenerRef.current) {
            google.maps.event.removeListener(dragEndListenerRef.current);
            dragEndListenerRef.current = null;
        }

        // Add new listener if draggable and onMarkerDragEnd is provided
        if (draggable && onMarkerDragEnd) {
            dragEndListenerRef.current = markerRef.current.addListener('dragend', () => {
                const newPosition = markerRef.current?.position;
                if (newPosition && typeof newPosition.lat === 'function' && typeof newPosition.lng === 'function') {
                    onMarkerDragEnd({
                        lat: newPosition.lat(),
                        lng: newPosition.lng(),
                    });
                }
            });
        }

        return () => {
            if (dragEndListenerRef.current) {
                google.maps.event.removeListener(dragEndListenerRef.current);
                dragEndListenerRef.current = null;
            }
        };
    }, [draggable, onMarkerDragEnd]);

    // Update content
    useEffect(() => {
        if (rootRef.current && isMountedRef.current) {
            rootRef.current.render(<div {...rest}>{children}</div>);
        }
    }, [children, rest]);

    // Update click handler
    useEffect(() => {
        if (!markerRef.current || !isMountedRef.current) return;

        const listener = markerRef.current.addListener('click', () => {
            onClick?.();
        });

        return () => {
            google.maps.event.removeListener(listener);
        };
    }, [onClick]);

    return null;
};

export default GoogleMapAdvancedMarker;
